import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3 } from 'lucide-react';
import { SectionHeader } from './shared/SectionHeader';
import { TrendBadge } from './shared/TrendBadge';
import { PlatformLogo } from './shared/PlatformLogo';
import { SkeletonLoader } from './shared/SkeletonLoader';
import { formatCurrencyCompact, formatCurrencyUnit, formatStat } from '../utils/formatters';

const PLATFORM_COLORS = {
  'google-ads': '#4285F4',
  'meta': '#1877F2',
  'ga4': '#E37400',
  'criteo': '#F48120',
};

function PlatformCard({ platform, name, metrics, color, isLoading }) {
  if (isLoading) return <SkeletonLoader variant="card" />;
  if (!metrics || metrics.length === 0) return null;

  return (
    <div className="glass-card rounded-2xl overflow-hidden hover-lift transition-all duration-200">
      {/* Color accent bar */}
      <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
      <div className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <PlatformLogo platform={platform} size="md" />
          <h3 className="text-sm font-semibold text-gray-800">{name}</h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {metrics.map((m) => (
            <div key={m.label}>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{m.label}</p>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <span className="text-sm font-bold text-gray-900">{m.value}</span>
                {m.trend != null && <TrendBadge value={m.trend} size="sm" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PlatformSummary({
  googleAdsSummary, metaAdsSummary, criteoAdsSummary,
  comparisonAggregates,
  isLoading,
}) {
  const cur = comparisonAggregates?.cur || {};

  const platforms = [
    googleAdsSummary && (googleAdsSummary.totalCost > 0 || googleAdsSummary.totalGoogleAdsRevenue > 0) && {
      platform: 'google-ads',
      name: 'Google Ads',
      color: PLATFORM_COLORS['google-ads'],
      metrics: [
        { label: 'Cost', value: formatCurrencyCompact(googleAdsSummary.totalCost) },
        { label: 'Clicks', value: formatStat(googleAdsSummary.totalClicks) },
        { label: 'Sessions', value: formatStat(googleAdsSummary.totalSessions) },
        { label: 'Conversions', value: formatStat(googleAdsSummary.totalConversions) },
        { label: 'Revenue', value: formatCurrencyCompact(googleAdsSummary.totalGa4Revenue) },
        { label: 'CPC', value: formatCurrencyUnit(googleAdsSummary.cpc) },
        { label: 'CPS', value: formatCurrencyUnit(googleAdsSummary.cps) },
        { label: 'ROAS', value: `${(googleAdsSummary.roas || 0).toFixed(2)}x` },
      ],
    },
    metaAdsSummary && (metaAdsSummary.totalSpend > 0 || metaAdsSummary.totalMetaRevenue > 0) && {
      platform: 'meta',
      name: 'Meta Ads',
      color: PLATFORM_COLORS['meta'],
      metrics: [
        { label: 'Spend', value: formatCurrencyCompact(metaAdsSummary.totalSpend) },
        { label: 'Clicks', value: formatStat(metaAdsSummary.totalClicks) },
        { label: 'Sessions', value: formatStat(metaAdsSummary.totalSessions) },
        { label: 'Conversions', value: formatStat(metaAdsSummary.totalConversions) },
        { label: 'Revenue', value: formatCurrencyCompact(metaAdsSummary.totalGa4Revenue) },
        { label: 'CPC', value: formatCurrencyUnit(metaAdsSummary.cpc) },
        { label: 'CPS', value: formatCurrencyUnit(metaAdsSummary.cps) },
        { label: 'ROAS', value: `${(metaAdsSummary.roas || 0).toFixed(2)}x` },
      ],
    },
    {
      platform: 'ga4',
      name: 'Google Analytics 4',
      color: PLATFORM_COLORS['ga4'],
      metrics: [
        { label: 'Sessions', value: formatStat(cur.sessions) },
        { label: 'Users', value: formatStat(cur.users) },
        { label: 'Transactions', value: formatStat(cur.eventCount) },
        { label: 'Revenue', value: formatCurrencyCompact(cur.totalRevenue) },
        { label: 'Conv. Rate', value: `${(comparisonAggregates?.curCR || 0).toFixed(2)}%` },
      ],
    },
    criteoAdsSummary && criteoAdsSummary.totalCost > 0 && {
      platform: 'criteo',
      name: 'Criteo',
      color: PLATFORM_COLORS['criteo'],
      metrics: [
        { label: 'Cost', value: formatCurrencyCompact(criteoAdsSummary.totalCost) },
        { label: 'Clicks', value: formatStat(criteoAdsSummary.totalClicks) },
        { label: 'Revenue', value: formatCurrencyCompact(criteoAdsSummary.totalCriteoRevenue) },
        { label: 'ROAS', value: `${(criteoAdsSummary.roas || 0).toFixed(2)}x` },
      ],
    },
  ].filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.5 }}
      className="mb-6"
    >
      <SectionHeader title="Platform Summary" icon={BarChart3} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {platforms.map((p) => (
          <PlatformCard key={p.platform} {...p} isLoading={isLoading} />
        ))}
      </div>
    </motion.div>
  );
}
